import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface DiscoveryParams {
  industry: string;
  targetMarket?: string;
  keywords?: string;
  sources?: string[];
}

interface DiscoveredLead {
  id: string;
  company_name: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  lead_score: number;
  ai_conversion_score: number;
}

export function useMarketDiscovery() {
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveredLeads, setDiscoveredLeads] = useState<DiscoveredLead[]>([]);
  const queryClient = useQueryClient();

  const discoverLeads = async (params: DiscoveryParams) => {
    setIsDiscovering(true);
    setDiscoveredLeads([]);

    try {
      // Get tenant_id from profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('id', user.id)
        .single();

      if (!profile?.tenant_id) throw new Error('No tenant found');

      const { data, error } = await supabase.functions.invoke('ai-market-discovery', {
        body: {
          industry: params.industry,
          targetMarket: params.targetMarket,
          keywords: params.keywords,
          sources: params.sources,
          tenantId: profile.tenant_id,
        },
      });

      if (error) throw error;

      if (data?.error) {
        if (data.error.includes('Rate limit')) {
          toast.error('Rate limit reached. Please try again in a moment.');
        } else {
          toast.error(data.error);
        }
        return [];
      }

      if (data?.success && data.leads) {
        setDiscoveredLeads(data.leads);
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        toast.success(data.message || `Discovered ${data.leads.length} leads`);
        return data.leads;
      }

      toast.info(data?.message || 'No leads found');
      return [];
    } catch (err: any) {
      console.error('Market discovery error:', err);
      toast.error(err.message || 'Failed to discover leads');
      return [];
    } finally {
      setIsDiscovering(false);
    }
  };

  return {
    isDiscovering,
    discoveredLeads,
    discoverLeads,
  };
}
